/**
 * Generic React Router query-param hook helpers.
 *
 * Page-specific param shape is owned by `src/lib/urlParams.ts`; this file
 * only exposes the low-level "give me the URLSearchParams" hook so tests
 * can verify either layer independently.
 */

import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';

export function useQuery(): URLSearchParams {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

/**
 * Memoised accessor — returns an object keyed by the supplied names with
 * the raw string value (or `null` if absent). Kept generic; `urlParams.ts`
 * builds the typed/validated shapes on top.
 */
export function useQueryParams<TKeys extends readonly string[]>(
  paramNames: TKeys,
): { [K in TKeys[number]]: string | null } {
  const query = useQuery();
  return useMemo(() => {
    const out = {} as { [K in TKeys[number]]: string | null };
    for (const name of paramNames) {
      (out as Record<string, string | null>)[name] = query.get(name);
    }
    return out;
  }, [query, paramNames]);
}
